import { gql } from "graphql-request";
import { createGraphQLClient } from "./graphql.service";
import { PaginationData } from "./entity-instance.service";

export interface RelationOpsPayload {
  connected?: Array<{ id: string }>;
  disconnected?: Array<{ id: string }>;
  /** Typo'd variant emitted by the backend in some code paths. */
  disonnected?: Array<{ id: string }>;
}

export interface EntityInstanceHistoryEvent {
  ts: string;
  event_name: string;
  pylo_app_id?: string | null;
  properties: {
    fields_updated?: Record<string, unknown>;
    fields_previous?: Record<string, unknown>;
    relations_updated?: Record<string, RelationOpsPayload>;
    entity_instance?: Record<string, unknown>;
    pylo_user_id?: string | null;
  };
}

export interface EntityInstanceHistoryResponse {
  data: EntityInstanceHistoryEvent[];
  pagination: PaginationData;
}

export const getEntityInstanceHistory = async ({
  entityName,
  entityInstanceId,
  page = 1,
  perPage = 50,
}: {
  entityName: string;
  entityInstanceId: string;
  page?: number;
  perPage?: number;
}): Promise<EntityInstanceHistoryResponse> => {
  const client = createGraphQLClient({ endpointName: "pyloEventList" });

  const doc = gql`
    query entityInstanceHistory(
      $eventNames: [String!]!
      $instanceId: String!
      $page: Int!
      $perPage: Int!
    ) {
      pyloEventList(
        filter: {
          query: [
            {
              and: [
                { condition: { field: "event_name", operator: in, values: $eventNames } }
                {
                  or: [
                    {
                      condition: {
                        field: "properties.entity_instance.id"
                        operator: equal
                        value: $instanceId
                      }
                    }
                    {
                      condition: {
                        field: "properties.fields_updated.id"
                        operator: equal
                        value: $instanceId
                      }
                    }
                  ]
                }
              ]
            }
          ]
          sortby: [{ field: "ts", order: desc }]
        }
        pagination: { page: $page, per_page: $perPage }
        select_fields: ["ts", "event_name", "pylo_app_id", "properties"]
      ) {
        data
        pagination {
          total
          has_more_pages
          current_page
          per_page
          last_page
        }
      }
    }
  `;

  const variables = {
    eventNames: [
      `entity.create.${entityName}`,
      `entity.update.${entityName}`,
      `entity.delete.${entityName}`,
    ],
    instanceId: entityInstanceId,
    page,
    perPage,
  };
  const response = await client.request<{
    pyloEventList: EntityInstanceHistoryResponse;
  }>(doc, variables);

  return response.pyloEventList;
};

export interface PyloUserMini {
  id: string;
  email: string;
  user_profile?: {
    data?: {
      firstname?: string | null;
      lastname?: string | null;
    } | null;
  } | null;
  pylo_api_key?: {
    data?: {
      id: string;
      name?: string | null;
    } | null;
  } | null;
}

export const getPyloUsersByIds = async (ids: string[]): Promise<PyloUserMini[]> => {
  if (ids.length === 0) return [];

  const client = createGraphQLClient();
  const doc = gql`
    query pyloUsersByIds($ids: [String!]!) {
      pyloUserList(
        pagination: { per_page: 100, page: 1 }
        filter: { query: [{ condition: { field: "id", operator: in, values: $ids } }] }
      ) {
        data {
          id
          email
          user_profile {
            data {
              firstname
              lastname
            }
          }
          pylo_api_key {
            data {
              id
              name
            }
          }
        }
      }
    }
  `;
  const response = await client.request<{
    pyloUserList: { data: PyloUserMini[] };
  }>(doc, { ids });
  return response.pyloUserList.data;
};

export interface PyloUserDisplay {
  label: string;
  isApiKey: boolean;
}

export const getPyloUserDisplay = (user: PyloUserMini): PyloUserDisplay => {
  const apiKey = user.pylo_api_key?.data;
  if (apiKey) {
    return { label: apiKey.name?.trim() || "API key", isApiKey: true };
  }
  const profile = user.user_profile?.data;
  const firstname = profile?.firstname?.trim();
  const lastname = profile?.lastname?.trim();
  if (firstname && lastname) return { label: `${firstname} ${lastname}`, isApiKey: false };
  if (firstname) return { label: firstname, isApiKey: false };
  if (lastname) return { label: lastname, isApiKey: false };
  return { label: user.email, isApiKey: false };
};
